/* eslint-disable max-lines */
import {
  AIRecommendation,
  Escalation,
  Location,
  UnifiedMonitoringProduct,
} from '@hakimo-ui/hakimo/types';
import { Button } from '@hakimo-ui/shared/ui-base';
import { PlusIcon } from '@heroicons/react/24/outline';
import { ContactRound, ExternalLink, FileText } from 'lucide-react';
import { useState } from 'react';
import LocationContacts from '../location-contacts/LocationContacts';
import {
  CARD_FILLS,
  EscalateFillsContext,
} from '../sop-workflow/escalate/fills';
import { SnapshotUploader } from '../sop-workflow/escalate/SnapshotUploader';
import { TopSituation } from '../sop-workflow/util';
import ResolveEscalationModal from '../unified-alarm-group/header/ResolveEscalationModal';
import { useSavedDescription } from '../unified-alarm-group/header/useSavedDescription';
import { CollapsibleCard } from './CollapsibleCard';
import { MUTED_ICON_CLASS } from './sop-card-utils';

const CONTACTS_BOX = 'max-h-80 overflow-y-auto px-2';

interface Props {
  locationId: string;
  locationAlarmId: string;
  alarmGroupId?: string;
  locationTenantId?: string;
  escalationId?: string;
  siteAddress: string;
  mapEmbedUrl?: string;
  product?: UnifiedMonitoringProduct;
  timezone?: string;
  escalation?: Escalation;
  location?: Location;
  cameraNames?: string[];
  aiOperatorRep?: AIRecommendation;
  onAddEvent?: (action: TopSituation) => void;
  onResolveEscalation?: (
    comment?: string,
    reason?: string,
    category?: string
  ) => void;
}

export function StepEscalationActions(props: Props) {
  const {
    locationId,
    locationAlarmId,
    alarmGroupId,
    locationTenantId,
    escalationId,
    siteAddress,
    mapEmbedUrl,
    product,
    timezone,
    escalation,
    location,
    cameraNames,
    aiOperatorRep,
    onAddEvent,
    onResolveEscalation,
  } = props;

  const [showResolveModal, setShowResolveModal] = useState(false);
  const [description, onDescriptionSaved] = useSavedDescription(
    escalation?.description ?? ''
  );

  const canOpenResolveModal = !!escalation && !!location;

  return (
    <div className="space-y-3">
      <CollapsibleCard
        variant="surface"
        title="Location Contacts"
        collapsible={false}
        icon={<ContactRound className={MUTED_ICON_CLASS} />}
      >
        <div className={CONTACTS_BOX}>
          <LocationContacts
            key={escalationId || 'no-escalation'}
            locationId={+locationId}
            locationTenantId={locationTenantId}
            locationAlarmId={locationAlarmId}
            alarmGroupId={alarmGroupId}
            escalationId={escalationId}
            product={product}
            timezone={timezone}
            showTitle={false}
          />
        </div>
      </CollapsibleCard>

      <SiteAddressBox siteAddress={siteAddress} mapEmbedUrl={mapEmbedUrl} />

      <CollapsibleCard
        variant="surface"
        title="Add Event"
        icon={<PlusIcon className={MUTED_ICON_CLASS} />}
        padding="p-3.5"
        bodyGap="pt-3.5"
      >
        <EscalateFillsContext.Provider value={CARD_FILLS}>
          <SnapshotUploader
            bare
            locationAlarmId={locationAlarmId}
            product={product}
            timezone={timezone}
            showTitle={false}
            onUploadSuccess={onAddEvent}
          />
        </EscalateFillsContext.Provider>
      </CollapsibleCard>

      <div className="!mt-3 space-y-1">
        <Button
          variant="success"
          onClick={() => setShowResolveModal(true)}
          disabled={!canOpenResolveModal}
          className="w-full justify-center"
        >
          Resolve
        </Button>
        {!canOpenResolveModal && (
          <p className="text-onlight-text-2 dark:text-ondark-text-2 text-center text-xs">
            Waiting for the escalation to be confirmed before it can be
            resolved.
          </p>
        )}
      </div>

      {showResolveModal && escalation && location && (
        <ResolveEscalationModal
          alarmId={locationAlarmId}
          escalation={{ ...escalation, description }}
          onDescriptionSaved={onDescriptionSaved}
          location={location}
          product={product ?? UnifiedMonitoringProduct.REMOTE_GUARDING}
          cameraNames={cameraNames}
          aiOperatorRep={aiOperatorRep}
          onResolveEscalation={onResolveEscalation}
          onClose={() => setShowResolveModal(false)}
        />
      )}
    </div>
  );
}

interface SiteAddressBoxProps {
  siteAddress: string;
  mapEmbedUrl?: string;
}

function SiteAddressBox({ siteAddress, mapEmbedUrl }: SiteAddressBoxProps) {
  const directionsHref = siteAddress
    ? `https://maps.google.com/?q=${encodeURIComponent(siteAddress)}`
    : undefined;

  return (
    <CollapsibleCard
      variant="surface"
      title="Site Address"
      icon={<FileText className={MUTED_ICON_CLASS} />}
      defaultExpanded={false}
      summary={
        <div className="flex items-start justify-between gap-2">
          <div className="text-onlight-text-2 dark:text-ondark-text-2 min-w-0 whitespace-pre-line text-sm [overflow-wrap:anywhere]">
            {siteAddress || '—'}
          </div>
          {directionsHref && (
            <a
              href={directionsHref}
              target="_blank"
              rel="noopener noreferrer"
              className="text-onlight-text-2 dark:text-ondark-text-2 hover:text-onlight-text-1 dark:hover:text-ondark-text-1 flex-shrink-0"
              title="Open in Google Maps"
            >
              <ExternalLink className="h-4 w-4" />
            </a>
          )}
        </div>
      }
    >
      {mapEmbedUrl && (
        <div className="relative h-36 overflow-hidden rounded-lg">
          <iframe
            src={mapEmbedUrl}
            title="Site location map"
            className="absolute left-0 top-0 h-56 w-[800px] origin-top-left scale-[0.65]"
            loading="lazy"
            referrerPolicy="no-referrer-when-downgrade"
          />
        </div>
      )}
    </CollapsibleCard>
  );
}

export default StepEscalationActions;
